import type {
  PutPrescheduledAppointmentData,
  GetCustomerList,
  GetCompanyList,
  PostUserAppointmentList,
  GetUsersServicesForAppointments,
  PostAppointment,
  PostPrescheduled,
  PostPrescheduledUserAppointment,
  PutRescheduleAppointment,
  GetPosServicesByUserAppointmentId,
  GetCalculatePaymentPrice,
  GetAppointmentLog,
  PostReturnValidOptions,
  PostValidateSubscriptionUse,
  PostSearchPrescheduledAppointments,
  PutAppointment,
  GetAppointment,
  DeleteAppointment,
  GetAppointmentCancelPreflight,
} from "@/server-types";
import { keepPreviousData } from "@tanstack/react-query";
import { del, get, post, put } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";
import { queryClient } from "..";
import { retryUnlessQueryTimedOut } from "@/utils/api-error";

export const useGetAppointmentByUserAppointmentId = ({
  userAppointmentId,
  enabled,
}: {
  userAppointmentId?: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getAppointmentByUserAppointmentId", userAppointmentId],
    queryFn: () =>
      get<GetPosServicesByUserAppointmentId>(
        `/appointment/user-appointments/${userAppointmentId!}`,
      ),
    enabled: userAppointmentId != null && enabled,
  });
};

export const useGetAppointment = ({
  appointmentId,
  enabled,
}: {
  appointmentId?: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getAppointment", appointmentId],
    queryFn: () =>
      get<GetAppointment>(`/calendar/appointment/${appointmentId!}`),
    enabled: appointmentId != null && enabled,
  });
};

export const getAppointment = (appointmentId: number) => {
  return get<GetAppointment>(`/calendar/appointment/${appointmentId}`);
};

export const useGetCalculatePaymentPrice = (
  query: GetCalculatePaymentPrice["query"],
  enabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: [
      "getCalculatePaymentPrice",
      query,
      query.appointmentParts,
      query.paymentDetails,
    ],
    queryFn: () =>
      get<GetCalculatePaymentPrice>(
        `/appointment/calculatePaymentPrice`,
        query,
      ),
    retry: false,
    displayNotification: false,
    enabled:
      enabled && query?.appointmentParts && query?.appointmentParts.length > 0,
  });
};

export const useCustomerList = (
  body: GetCustomerList["body"],
  enabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["getCustomerList", body],
    queryFn: () => post<GetCustomerList>("/appointment/getCustomerList", body),
    enabled,
    retry: retryUnlessQueryTimedOut,
  });
};

export const useCompanyList = (
  body: GetCompanyList["body"],
  enabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["getCompanyList", body],
    queryFn: () => post<GetCompanyList>("/appointment/getCompanyList", body),
    enabled,
  });
};

const ALL_MATCHING_CUSTOMERS_PER_PAGE = 1000;

/**
 * Resolves the ids of *every* customer matching the given filter by paging
 * through the customer list endpoint. Backs the customer list "select all
 * matching" action so the whole filtered result set can be targeted by bulk
 * SMS without manually selecting each page. Reuses the same endpoint as the
 * list, so the caller's permission and tenant boundaries are preserved.
 */
export const useGetAllMatchingCustomerIds = () => {
  return useCustomMutation({
    mutationFn: async (body: GetCustomerList["body"]): Promise<number[]> => {
      const fetchPage = (page: number) =>
        post<GetCustomerList>("/appointment/getCustomerList", {
          ...body,
          page,
          perPage: ALL_MATCHING_CUSTOMERS_PER_PAGE,
        });

      const firstPage = await fetchPage(1);
      const customerIds = firstPage.customers.map((customer) =>
        Number(customer.customerId),
      );

      const pageCount = Math.max(
        1,
        Math.ceil(
          firstPage.pagination.totalItems / ALL_MATCHING_CUSTOMERS_PER_PAGE,
        ),
      );

      if (pageCount > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) =>
            fetchPage(index + 2),
          ),
        );

        for (const pageResult of remainingPages) {
          for (const customer of pageResult.customers) {
            customerIds.push(Number(customer.customerId));
          }
        }
      }

      return Array.from(new Set(customerIds));
    },
  });
};

export const useUserAppointmentList = (
  body: PostUserAppointmentList["body"],
  disable?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["getCustomerList", body],
    queryFn: () =>
      post<PostUserAppointmentList>(
        "/appointment/user_appointments/list",
        body,
      ),
    enabled: !disable,
  });
};

export const useUsersServicesForAppointments = () => {
  return useCustomQuery({
    queryKey: ["getUsersServices"],
    queryFn: () =>
      get<GetUsersServicesForAppointments>(
        "/appointment/user_appointments/users_services",
      ),
  });
};

/**
 * PUT /appointment
 */
export const useRescheduleAppointment = () => {
  return useCustomMutation({
    mutationKey: ["rescheduleAppointment"],
    mutationFn: ({
      appointmentId,
      body,
    }: {
      appointmentId: number;
      body: PutRescheduleAppointment["body"];
    }) =>
      put<PutRescheduleAppointment>(
        `/appointment/${appointmentId}/reschedule`,
        body,
      ),
  });
};

/**
 * POST /appointment
 */
export const usePostAppointment = (body: PostAppointment["body"]) => {
  return post<PostAppointment>("/appointment", body);
};

/**
 * POST /appointment/prescheduled
 */
export const usePostPrescheduled = () => {
  return useCustomMutation({
    mutationKey: ["postPrescheduled"],
    mutationFn: (body: PostPrescheduled["body"]) =>
      post<PostPrescheduled>(`/appointment/prescheduled`, body),
  });
};

/**
 * POST /appointment/prescheduled/customer
 */
export const usePostPrescheduledUserAppointment = () => {
  return useCustomMutation({
    mutationKey: ["postPrescheduledUserAppointment"],
    mutationFn: (body: PostPrescheduledUserAppointment["body"]) =>
      post<PostPrescheduledUserAppointment>(
        `/appointment/prescheduled/customer`,
        body,
      ),
    onSuccess: (_, body) => {
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
    },
  });
};

/**
 * POST /appointment/validOptions
 */
export const usePostValidOptions = () => {
  return useCustomMutation({
    mutationKey: ["validOptions"],
    mutationFn: (body: PostReturnValidOptions["body"]) =>
      post<PostReturnValidOptions>(`/appointment/validOptions`, body),
  });
};

/**
 * POST /appointment/validOptions
 */
export const usePostReturnValidOptions = (
  body: PostReturnValidOptions["body"],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: [
      "returnValidOptions",
      body.selectedServices.length,
      body.selectedServices.map((s) => s.serviceId),
      body.selectedServices.map((s) => s.users),
      body,
    ],
    queryFn: () =>
      post<PostReturnValidOptions>(`/appointment/validOptions`, body),
    enabled,
    placeholderData: keepPreviousData,
  });
};

/**
 * GET /appointment/appointment/{appointmentId}/log
 */
export const useAppointmentLog = (appointmentId: number) => {
  return useCustomQuery({
    queryKey: ["getAppointmentLog", appointmentId],
    queryFn: () => get<GetAppointmentLog>(`/appointment/${appointmentId}/log`),
    enabled: typeof appointmentId === "number" && appointmentId !== -1,
  });
};

export const useGetPrescheduledAppointments = (
  body: PostSearchPrescheduledAppointments["body"],
  enabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["getPrescheduledAppointments", body],
    queryFn: () =>
      post<PostSearchPrescheduledAppointments>(
        "/appointment/prescheduled/search",
        body,
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * POST /appointment/validateSubscriptionUse
 */
export const useValidateSubscriptionUse = () => {
  return useCustomMutation({
    mutationKey: ["validateSubscriptionUse"],
    mutationFn: (body: PostValidateSubscriptionUse["body"]) =>
      post<PostValidateSubscriptionUse>(
        `/appointment/validateSubscriptionUse`,
        body,
      ),
  });
};

export const usePutPrescheduledAppointmentData = () => {
  return useCustomMutation({
    mutationKey: ["putPrescheduledAppointmentData"],
    mutationFn: (props: {
      params: PutPrescheduledAppointmentData["params"];
      body: PutPrescheduledAppointmentData["body"];
    }) =>
      put<PutPrescheduledAppointmentData>(
        `/appointment/prescheduled/${props.params.appointmentId}`,
        props.body,
      ),
    displayNotification: true,
  });
};

export const usePutAppointment = () => {
  return useCustomMutation({
    mutationKey: ["putAppointment"],
    mutationFn: ({
      appointmentId,
      body,
    }: {
      appointmentId: number;
      body: PutAppointment["body"];
    }) => put<PutAppointment>(`/calendar/appointment/${appointmentId}`, body),
    onSuccess: (_, { body }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: [
          "customer-subscription-by-id",
          body.customerData.customerSubscriptionId,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

/**
 * DELETE /calendar/appointment/:appointmentId
 */
export const useDeleteAppointment = () => {
  return useCustomMutation({
    mutationFn: ({
      appointmentId,
      body,
    }: {
      appointmentId: number;
      body: DeleteAppointment["body"];
    }) =>
      del<DeleteAppointment>(`/calendar/appointment/${appointmentId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

/**
 * GET /calendar/appointment/:appointmentId/cancel-preflight
 */
export const useGetAppointmentCancelPreflight = ({
  appointmentId,
  enabled = true,
}: {
  appointmentId?: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["appointment-cancel-preflight", appointmentId],
    queryFn: () =>
      get<GetAppointmentCancelPreflight>(
        `/calendar/appointment/${appointmentId!}/cancel-preflight`,
      ),
    enabled: appointmentId != null && enabled,
  });
};
